import { supabase } from '@/lib/supabase';
import { COA_HEADER_CODES } from '@/app/data/defaultCoASeed';

export interface Account {
  id?: string;
  company_id: string;
  branch_id?: string;
  code?: string;
  name: string;
  type: 'Cash' | 'Bank' | 'Mobile Wallet' | 'cash' | 'bank' | 'mobile_wallet' | 'asset' | 'liability' | 'equity' | 'revenue' | 'expense' | string;
  balance: number;
  parent_id?: string | null;
  is_active?: boolean;
  /** COA section header — excluded from payment account pickers. */
  is_group?: boolean;
  // Optional fields that may not exist in all schema versions
  subtype?: string;
  opening_balance?: number;
  current_balance?: number;
  is_system?: boolean;
  description?: string;
  created_at?: string;
  updated_at?: string;
  /** Party subledger: FK to contacts (migration 20260364). */
  linked_contact_id?: string | null;
}

const ACCOUNTS_PAGE_SIZE = 1000;

function sortAccountsByCodeThenName<T extends { code?: string | null; name?: string | null }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const ca = String(a.code ?? '').trim();
    const cb = String(b.code ?? '').trim();
    const na = Number(ca.replace(/\D/g, '')) || 0;
    const nb = Number(cb.replace(/\D/g, '')) || 0;
    if (na !== nb) return na - nb;
    if (ca !== cb) return ca.localeCompare(cb, undefined, { numeric: true });
    return String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' });
  });
}

export const accountService = {
  // Get all accounts - with company_id filter (paginated past PostgREST ~1000 cap; ordered by code)
  async getAllAccounts(companyId: string, branchId?: string) {
    try {
      const bid = branchId && String(branchId).trim() !== '' && String(branchId) !== 'all' ? String(branchId).trim() : '';
      const uuidOk = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(bid);

      const fetchPage = async (from: number, opts: { useCompany: boolean; useBranch: boolean }) => {
        let query = supabase.from('accounts').select('*').order('code', { ascending: true }).range(from, from + ACCOUNTS_PAGE_SIZE - 1);
        if (opts.useCompany && companyId) query = query.eq('company_id', companyId);
        if (opts.useBranch && uuidOk) query = query.or(`branch_id.is.null,branch_id.eq.${bid}`);
        return query;
      };

      const fetchAllPages = async (opts: { useCompany: boolean; useBranch: boolean }) => {
        const out: Account[] = [];
        for (let from = 0; ; from += ACCOUNTS_PAGE_SIZE) {
          const { data, error } = await fetchPage(from, opts);
          if (error) throw error;
          const chunk = (data || []) as Account[];
          out.push(...chunk);
          if (chunk.length < ACCOUNTS_PAGE_SIZE) break;
        }
        return sortAccountsByCodeThenName(out);
      };

      try {
        return await fetchAllPages({ useCompany: true, useBranch: uuidOk });
      } catch (error: unknown) {
        const err = error as { message?: string; details?: string; code?: string };
        // Older DBs without branch_id: retry without branch OR filter
        if (
          uuidOk &&
          (err.message?.includes('branch_id') || String(err.details || '').includes('branch_id'))
        ) {
          console.warn('[ACCOUNT SERVICE] branch_id not available on accounts, retrying without branch scope');
          return await fetchAllPages({ useCompany: true, useBranch: false });
        }
        // If error related to company_id column, retry without it
        if (err.message?.includes('company_id') || err.code === 'PGRST204') {
          console.warn('[ACCOUNT SERVICE] company_id column not found, fetching all accounts');
          return await fetchAllPages({ useCompany: false, useBranch: false });
        }
        throw error;
      }
    } catch (error) {
      console.error('[ACCOUNT SERVICE] Error fetching accounts:', error);
      // Final fallback - return empty array to prevent crashes
      return [];
    }
  },

  // Get single account
  async getAccount(id: string) {
    const { data, error } = await supabase
      .from('accounts')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  /** Alias for getAccount (used by deleteAccount and callers expecting getAccountById) */
  async getAccountById(id: string) {
    return this.getAccount(id);
  },

  /**
   * Get accounts for branch default dropdowns (cash, bank, wallet only).
   * Single source of truth: company_id + is_active + operational role.
   * Does NOT depend on module toggle or UI mode. Uses type; if DB has account_role column, it is used first.
   */
  async getAccountsForBranchDefaults(companyId: string) {
    return this.getPaymentAccountsOnly(companyId);
  },

  /**
   * Payment accounts only: Cash, Bank, Mobile Wallet (real money movement).
   * Use for: Paid From, Payment account (Cr), any dropdown that must show only money accounts.
   * Excludes: AP, AR, expense, revenue, payable, receivable, production, shipping, control accounts.
   */
  async getPaymentAccountsOnly(companyId: string, _branchId?: string) {
    // Slim query — do not pull full COA via getAllAccounts just for a dropdown.
    const { data, error } = await supabase
      .from('accounts')
      .select('id, name, code, type, is_active, is_group, parent_id')
      .eq('company_id', companyId)
      .eq('is_active', true)
      .or(
        'type.ilike.%cash%,type.ilike.%bank%,type.ilike.%wallet%,code.eq.1000,code.eq.1010,code.eq.1020,name.ilike.%cash%,name.ilike.%bank%,name.ilike.%wallet%',
      )
      .limit(200);
    if (error) {
      if (import.meta.env?.DEV) console.warn('[getPaymentAccountsOnly] slim query failed, fallback:', error.message);
      const all = await this.getAllAccounts(companyId);
      return this.filterPaymentAccounts(all || []);
    }
    return this.filterPaymentAccounts(data || []);
  },

  filterPaymentAccounts(all: any[]) {
    const active = (all || []).filter((a: any) => a.is_active !== false);
    const roleOrType = (a: any) =>
      String(a.account_role ?? a.type ?? '').toLowerCase().trim();
    const name = (a: any) => String(a.name ?? '').toLowerCase();
    const code = (a: any) => String(a.code ?? '').toLowerCase();
    return active.filter((a: any) => {
      if (a.is_group === true) return false;
      const r = roleOrType(a);
      const n = name(a);
      const rawCode = String(a.code ?? '').trim();
      if (COA_HEADER_CODES.has(rawCode)) return false;
      const c = rawCode.toLowerCase();
      if (n.includes('payable') || n.includes('receivable') || n.includes('ar ') || n.includes(' ap ') || c.startsWith('2') || c === '1100' || c === '2000' || c === '2010') return false;
      if (n.includes('expense') && !n.includes('payment')) return false;
      if (n.includes('revenue') || n.includes('income') || n.includes('production') || n.includes('shipping') || n.includes('courier')) return false;
      return (
        r === 'cash' ||
        r === 'bank' ||
        r === 'wallet' ||
        r === 'mobile_wallet' ||
        r === 'mobile wallet' ||
        r.includes('cash') ||
        r.includes('bank') ||
        r.includes('wallet') ||
        c === '1000' ||
        c === '1010' ||
        c === '1020' ||
        n.includes('cash') ||
        n.includes('bank') ||
        n.includes('wallet')
      );
    });
  },

  // Create account
  async createAccount(account: Partial<Account>) {
    const trimmedCode = String(account.code ?? '').trim();
    const code =
      trimmedCode ||
      `GEN-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

    // Clean data - only include fields that exist in actual schema
    // Actual schema may or may not include description (add via 17_accounts_description.sql)
    const cleanData: any = {
      company_id: account.company_id,
      code,
      name: account.name,
      type: account.type,
      balance: account.balance || 0,
      is_active: account.is_active !== false,
    };

    if (account.parent_id !== undefined && account.parent_id !== null) {
      cleanData.parent_id = account.parent_id;
    }
    if (account.description !== undefined && account.description !== null && String(account.description).trim() !== '') {
      cleanData.description = String(account.description).trim();
    }
    if (account.is_group !== undefined) {
      cleanData.is_group = !!account.is_group;
    }
    if (account.linked_contact_id !== undefined && account.linked_contact_id !== null && String(account.linked_contact_id).trim() !== '') {
      cleanData.linked_contact_id = String(account.linked_contact_id).trim();
    }

    let result = await supabase.from('accounts').insert(cleanData).select().single();

    if (result.error && result.error.code === 'PGRST204' && result.error.message?.includes('description')) {
      delete cleanData.description;
      result = await supabase.from('accounts').insert(cleanData).select().single();
    }
    if (result.error && result.error.code === 'PGRST204' && String(result.error.message || '').includes('is_group')) {
      delete cleanData.is_group;
      result = await supabase.from('accounts').insert(cleanData).select().single();
    }
    if (result.error && result.error.code === 'PGRST204' && String(result.error.message || '').includes('linked_contact')) {
      delete cleanData.linked_contact_id;
      result = await supabase.from('accounts').insert(cleanData).select().single();
    }

    if (result.error) throw result.error;
    return result.data;
  },

  // Update account
  async updateAccount(id: string, updates: Partial<Account>) {
    // Clean data - only include fields that exist in actual schema
    const cleanData: any = {};
    
    if (updates.code !== undefined) cleanData.code = updates.code;
    if (updates.name !== undefined) cleanData.name = updates.name;
    if (updates.type !== undefined) cleanData.type = updates.type;
    if (updates.balance !== undefined) cleanData.balance = updates.balance;
    if (updates.is_active !== undefined) cleanData.is_active = updates.is_active;
    if (updates.parent_id !== undefined) cleanData.parent_id = updates.parent_id;
    if (updates.description !== undefined) cleanData.description = updates.description === '' ? null : updates.description;
    if (updates.is_group !== undefined) cleanData.is_group = updates.is_group;
    if (updates.linked_contact_id !== undefined) {
      cleanData.linked_contact_id =
        updates.linked_contact_id === null || updates.linked_contact_id === ''
          ? null
          : String(updates.linked_contact_id).trim();
    }
    
    // DO NOT include: account_type, branch_id, branch_name, subtype, opening_balance, current_balance, is_system, is_default_cash, is_default_bank
    
    // Filter out any non-existent fields that might have been passed
    const allowedFields = ['code', 'name', 'type', 'balance', 'is_active', 'parent_id', 'description', 'is_group', 'linked_contact_id'];
    const filteredData: any = {};
    for (const key of allowedFields) {
      if (cleanData[key] !== undefined) {
        filteredData[key] = cleanData[key];
      }
    }

    let result = await supabase.from('accounts').update(filteredData).eq('id', id).select().single();
    if (result.error && result.error.code === 'PGRST204' && result.error.message?.includes('description')) {
      delete filteredData.description;
      result = await supabase.from('accounts').update(filteredData).eq('id', id).select().single();
    }
    if (result.error && result.error.code === 'PGRST204' && String(result.error.message || '').includes('is_group')) {
      delete filteredData.is_group;
      result = await supabase.from('accounts').update(filteredData).eq('id', id).select().single();
    }
    if (result.error) throw result.error;
    return result.data;
  },

  /**
   * Get child accounts (accounts that have this account as parent).
   * Used to prevent deleting a parent that has sub-accounts.
   */
  async getChildAccounts(parentId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('accounts')
      .select('id, name')
      .eq('parent_id', parentId);

    if (error) throw error;
    return data || [];
  },

  // Delete account (soft delete)
  // 🔒 CORE ACCOUNTING BACKBONE RULE: Core payment accounts (Cash, Bank, Mobile Wallet) CANNOT be deleted
  // 🔒 PARENT RULE: Cannot delete an account that has sub-accounts (children)
  async deleteAccount(id: string, companyId?: string) {
    const children = await this.getChildAccounts(id);
    if (children.length > 0) {
      throw new Error(
        `Cannot delete "${(await this.getAccount(id))?.name || 'this account'}": it has ${children.length} sub-account(s). ` +
        `Remove or reassign sub-accounts first.`
      );
    }

    if (companyId) {
      const { defaultAccountsService } = await import('./defaultAccountsService');
      const account = await this.getAccount(id);
      
      if (account && defaultAccountsService.isCorePaymentAccount(account)) {
        throw new Error(
          `Cannot delete core payment account "${account.name}". ` +
          `Core accounts (Cash, Bank, Mobile Wallet) are mandatory and cannot be deleted. ` +
          `You can rename the account if needed.`
        );
      }
    }
    
    const { error } = await supabase
      .from('accounts')
      .update({ is_active: false })
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * PF-06: Operating expense accounts only (for user-facing expense pickers).
   * Excludes production/studio/shipping/courier cost accounts (5000, 5010, 5100, 5200, 5300 and names matching internal).
   */
  async getOperatingExpenseAccountsOnly(companyId: string) {
    const all = await this.getAllAccounts(companyId);
    const active = (all || []).filter((a: any) => a.is_active !== false);
    const expenseType = (a: any) => /expense|cogs|cost of sales/i.test(String(a.type ?? ''));
    const internalCodes = new Set(['5000', '5010', '5100', '5200', '5300']);
    const internalNames = /cost of production|cost of studio|shipping expense|extra expense|courier|payable|receivable/i;
    return active.filter((a: any) => {
      if (!expenseType(a)) return false;
      const code = String(a.code ?? '').trim();
      const name = String(a.name ?? '').toLowerCase();
      if (internalCodes.has(code)) return false;
      if (internalNames.test(name)) return false;
      return true;
    });
  },

  // Get accounts by type
  async getAccountsByType(companyId: string, type: 'Cash' | 'Bank' | 'Mobile Wallet') {
    const { data, error } = await supabase
      .from('accounts')
      .select('*')
      .eq('company_id', companyId)
      .eq('type', type)
      .eq('is_active', true)
      .order('name');

    if (error) throw error;
    return data;
  },
};
